/**
 * /locations/australia — National entry point.
 *
 * SEO-first landing page listing every state we cover, mail-out
 * pathway, partner-finder search, and the three package families
 * (tank / rainwater / bore + comprehensive). Genuinely national
 * copy — not a state-swap template.
 */
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Package, Search, Send, Droplets, ShieldCheck } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { SchemaJsonLd, breadcrumbSchema } from "@/lib/seo";
import { useSEO, SEO_DEFAULTS } from "@/lib/useSEO";
import { COUNTRIES, STATES } from "@/data/locationTree";

const BASE_URL = SEO_DEFAULTS.baseUrl;

export default function AustraliaHub() {
  const url = `${BASE_URL}/locations/australia`;
  useSEO({
    title: "Water Testing Australia — Aware Water Testing",
    description:
      "Independent laboratory water testing for every Australian home. Mail your sample from anywhere in Australia. Direct pathways for rainwater tank, bore water, and town supply.",
    url,
  });

  const activeStates = Object.values(STATES).filter((s) => s.regions.length > 0);
  const country = COUNTRIES.australia;

  const breadcrumbs = [
    { name: "Home", url: BASE_URL },
    { name: "Locations", url: `${BASE_URL}/locations` },
    { name: "Australia", url },
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="australia-hub-page">
      <Header />
      <SchemaJsonLd data={breadcrumbSchema(breadcrumbs)} />

      {/* Hero */}
      <section className="border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 py-14 md:py-20">
          <nav aria-label="Breadcrumb" className="text-xs text-slate-500 mb-4">
            <Link to="/" className="hover:text-slate-900">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/locations" className="hover:text-slate-900">Locations</Link>
            <span className="mx-2">/</span>
            <span className="text-slate-900 font-medium">Australia</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full bg-cyan-50 border border-cyan-200 text-cyan-800 px-3 py-1 text-xs font-semibold mb-4">
            <MapPin className="w-3.5 h-3.5" /> Australia-wide independent water testing
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900" data-testid="australia-hub-h1">
            Water testing for every Australian home.
          </h1>
          <p className="mt-4 max-w-2xl text-base text-slate-600">
            {country.tagline} Whether you&#39;re on a rainwater tank in North Queensland, a
            bore in the Adelaide Hills, or town supply in inner Melbourne — the
            Aware Water Testing service gets a laboratory report back to you the
            same way we do it for a lab-managed customer down the street.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/book-water-test">
              <Button className="rounded-full bg-cyan-600 hover:bg-cyan-700 text-white h-11 px-6" data-testid="australia-hub-book-cta">
                Book a test <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/mail-out-water-testing-kits">
              <Button variant="outline" className="rounded-full h-11 px-6" data-testid="australia-hub-mailout-cta">
                <Send className="w-4 h-4 mr-2" /> Mail-out sample kits
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* States with live coverage */}
      <section className="border-b border-slate-100 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <h2 className="text-base font-semibold text-slate-800">Where we operate</h2>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl">
            We ship sample kits Australia-wide and analyse them at independent
            partner laboratories. Currently we have on-the-ground partner support
            in the states below — with more coming online through 2026.
          </p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {activeStates.map((s) => (
              <Link
                key={s.slug}
                to={`/locations/${s.slug}`}
                className="group rounded-2xl bg-white border border-slate-200 hover:border-cyan-300 hover:shadow-md transition p-6"
                data-testid={`australia-hub-state-${s.slug}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-cyan-700 font-semibold">
                      Active state · {s.abbr}
                    </div>
                    <div className="mt-1 text-2xl font-bold text-slate-900">{s.name}</div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-cyan-600 group-hover:translate-x-0.5 transition" />
                </div>
                <p className="mt-3 text-sm text-slate-600 line-clamp-2">{s.tagline}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {s.regions.map((r) => (
                    <span key={r} className="inline-flex text-[11px] font-medium rounded-full bg-slate-100 text-slate-700 px-2 py-0.5">
                      {r.replace(/-/g, " ")}
                    </span>
                  ))}
                </div>
              </Link>
            ))}
            {/* Coming-soon placeholder — communicates roadmap without linking to empty pages */}
            <div className="rounded-2xl bg-white border border-dashed border-slate-300 p-6 flex items-center justify-between" data-testid="australia-hub-coming-soon">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  Coming through 2026
                </div>
                <div className="mt-1 text-lg font-semibold text-slate-800">
                  NSW · VIC · WA · SA · TAS · ACT · NT
                </div>
                <p className="mt-2 text-sm text-slate-500">
                  Mail your sample from any state today — partner-led regional
                  coverage rolling out.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Three pathways */}
      <section className="border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <h2 className="text-base font-semibold text-slate-800">Three ways your water reaches us</h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-slate-200 p-6" data-testid="australia-hub-pathway-mailout">
              <Send className="w-5 h-5 text-cyan-700" />
              <div className="mt-3 text-lg font-bold text-slate-900">Mail-out sample kit</div>
              <p className="mt-1 text-sm text-slate-600">
                Ships Australia-wide with pre-paid return postage. Suits anyone
                more than 60 minutes from a partner service area.
              </p>
              <Link to="/mail-out-water-testing-kits" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-cyan-700 hover:text-cyan-900">
                How mail-out works <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="rounded-2xl border border-slate-200 p-6" data-testid="australia-hub-pathway-partner">
              <MapPin className="w-5 h-5 text-cyan-700" />
              <div className="mt-3 text-lg font-bold text-slate-900">Local partner drop-off</div>
              <p className="mt-1 text-sm text-slate-600">
                In partner regions, sample bottles can be collected and dropped
                off in-person — with the same laboratory report back to you.
              </p>
              <Link to="/partner-network" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-cyan-700 hover:text-cyan-900">
                Find your local partner <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="rounded-2xl border border-slate-200 p-6" data-testid="australia-hub-pathway-onsite">
              <Droplets className="w-5 h-5 text-cyan-700" />
              <div className="mt-3 text-lg font-bold text-slate-900">Property Manager service</div>
              <p className="mt-1 text-sm text-slate-600">
                Rental compliance testing for real-estate managers, with
                landlord reimbursement built-in. Reporting per property, not per test.
              </p>
              <Link to="/property-managers" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-cyan-700 hover:text-cyan-900">
                Manager pricing <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="border-b border-slate-100 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 py-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          <TrustPoint icon={ShieldCheck} title="Independent Testing Laboratory">
            Every physical, chemical and microbial parameter is analysed at
            an independent partner laboratory in accordance with the Australian
            Drinking Water Guidelines.
          </TrustPoint>
          <TrustPoint icon={Package} title="One flat, all-inclusive price">
            No hidden postage. No sampling fee. No report-generation surcharge.
            The price you see is the price you pay, anywhere in Australia.
          </TrustPoint>
          <TrustPoint icon={Search} title="You keep the results">
            Every test creates a Water Health Passport for your property —
            searchable, portable, and useful the next time you sell, rent or
            renovate.
          </TrustPoint>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function TrustPoint({ icon: Icon, title, children }) {
  return (
    <div>
      <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center">
        <Icon className="w-5 h-5 text-cyan-700" />
      </div>
      <div className="mt-3 text-sm font-bold text-slate-900">{title}</div>
      <p className="mt-1 text-sm text-slate-600">{children}</p>
    </div>
  );
}
